// src/helpers/Router.js

import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import GlobalErrorListener from "../component/common/GlobalErrorListener.tsx";
import ErrorBoundary from "../component/common/ErrorBoundary.jsx";
import CustomDialog from "../component/common/CustomDialog.jsx";
import { useSessionInit } from "../hooks/useSessionInit.ts";
import { sanitizeUrl } from "../utils/analytics.ts";
import { RouteFallbackSkeleton } from "../component/common/Skeleton.jsx";

import Layout from "./Layout.js";

// 첫 진입 화면은 즉시 보여야 하므로 정적 import 로 유지
import Landing from "../pages/landing.jsx";
import Home from "../pages/home.jsx";
import AuthCallback from "../pages/auth/callback.jsx";

// 나머지 페이지는 라우트 단위 코드 스플리팅
const NotFoundPage = lazy(() => import("../pages/404.jsx"));
const Community = lazy(() => import("../pages/community/community.jsx"));
const SignIn = lazy(() => import("../pages/auth/signIn.jsx"));
const SignUpAgree = lazy(() => import("../pages/auth/signUpAgree.jsx"));
const SignUpUserInfo = lazy(() => import("../pages/auth/signUpUserInfo.jsx"));
const MyProfile = lazy(() => import("../pages/auth/myProfile.jsx"));
const CategoryPage = lazy(() => import("../pages/service/categroy.jsx"));
const ServiceDetailPage = lazy(() => import("../pages/service/serviceDetail.jsx"));
const SearchPage = lazy(() => import("../pages/service/search.jsx"));
const CommunityDetail = lazy(() => import("../pages/community/community_detail.jsx"));
const CommunityForm = lazy(() => import("../pages/community/community_form.jsx"));
const EmailSignIn = lazy(() => import("../pages/auth/emailSignIn.jsx"));
const HalalMap = lazy(() => import("../pages/halalMap/HalalMap.jsx"));
const Visa = lazy(() => import("../pages/visa.jsx"));
const Airport = lazy(() => import("../pages/airport/airport.jsx"));
const DeliveryHome = lazy(() => import("../pages/delivery/deliveryHome.jsx"));
const DeliveryVendor = lazy(() => import("../pages/delivery/deliveryVendor.jsx"));
const DeliveryOrders = lazy(() => import("../pages/delivery/deliveryOrders.jsx"));
const DeliveryOrderDetail = lazy(() => import("../pages/delivery/deliveryOrderDetail.jsx"));
const Booking = lazy(() => import("../pages/booking/booking.jsx"));
const Reservation = lazy(() => import("../pages/reservation/reservation_list.jsx"));
const ReservationDetail = lazy(() => import("../pages/reservation/reservation_detail.jsx"));
const Bookmark = lazy(() => import("../pages/bookmark/bookmark.jsx"));
const Reviews = lazy(() => import("../pages/reviews/reviews.jsx"));
const ReviewDetail = lazy(() => import("../pages/reviews/review_detail.jsx"));
const Notification = lazy(() => import("../pages/notification/notification.jsx"));
const PolicyScreen = lazy(() => import("../pages/policy.jsx"));
const PolicyDetail = lazy(() => import("../pages/policyDetail.jsx"));
const Coupon = lazy(() => import("../pages/auth/myCoupon.jsx"));
const CouponDetail = lazy(() => import("../pages/auth/couponDetail.jsx"));
const GuestBooking = lazy(() => import("../pages/booking/guest_booking.jsx"));
const AboutUs = lazy(() => import("../pages/about.jsx"));
const PartnerInquiry = lazy(() => import("../pages/partnerInquiry.jsx"));
const TripInquiry = lazy(() => import("../pages/tripInquiry.jsx"));
const ProviderProfilePage = lazy(() => import("../pages/service/providerProfile.jsx"));
const ProviderServicesPage = lazy(() => import("../pages/service/providerServices.jsx"));

/**
 * SPA 페이지뷰 추적.
 *
 * `gtag("config", ...)` 를 라우트마다 다시 부르면 안 된다 — config 는 페이지뷰
 * 전송이 아니라 «태그 초기화» 라, 이동할 때마다 세션·최초방문 판정이 다시
 * 일어난다. 실측(2026-08-17~19, korehalal 호스트): 실제 세션 113개인데
 * session_start 173건, 사용자 33명인데 first_visit 111건 — 1인당 3.4번.
 * 같은 기간 앱(Firebase SDK)은 first_open 50 = 신규 50 으로 멀쩡했다.
 *
 * 그래서 page_view 를 «이벤트» 로 보낸다. index.html 은 send_page_view:false
 * 라 자동 발송이 꺼져 있고, 첫 진입도 이 훅이 담당한다.
 *
 * 이 훅은 반드시 **모든 라우트를 감싸는 곳**에 있어야 한다. 예전엔 Layout 에
 * 있었는데 랜딩("/")이 Layout 밖이라 첫 화면이 집계에서 빠질 자리였다.
 */
function usePageTracking(location) {
  // 의존성은 location 객체가 아니라 «경로 문자열» 이어야 한다 — 이 앱은 마운트
  // 직후 같은 URL 로 replaceState 를 한 번 한다. 객체를 키로 잡으면 경로가 안
  // 바뀌었는데도 훅이 다시 돌아 같은 화면이 두 번 집계된다.
  //
  // 예약·배달 주문 uuid 는 조회·취소 권한이라 GA 로 나가면 안 된다 — sanitizeUrl 로
  // 가리고 쿼리·해시도 버린다. page_view 에만 넣으면 안 되고 `set` 으로 박아야 한다:
  // gtag 는 모든 이벤트에 현재 URL 을 붙이므로 /booking/<uuid> 에서 나가는 결제 퍼널
  // 이벤트(logEvent)가 그대로 새어 나간다.
  const path = location.pathname + location.search;

  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("set", {
      page_location: sanitizeUrl(window.location.href),
      page_referrer: sanitizeUrl(document.referrer),
    });
    window.gtag("event", "page_view", {
      page_path: sanitizeUrl(path),
      page_title: document.title,
    });
  }, [path]);
}

function AppRoutes() {
  const location = useLocation();
  usePageTracking(location);
  const ready = useSessionInit();

  if (location.pathname.endsWith('/auth/callback')) {
    return <AuthCallback />;
  }

  if (!ready) {
    return <RouteFallbackSkeleton />;
  }

  return (
    <Suspense fallback={<RouteFallbackSkeleton />}>
      <Routes>
        <Route path="/" element={<Landing />} />

        <Route element={<Layout />}>
          <Route path="/home" element={<Home />} />
          <Route path="/my-profile" element={<MyProfile />} />
          <Route path="/category" element={<CategoryPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/service/:uuid" element={<ServiceDetailPage />} />
          <Route path="/provider/:uuid" element={<ProviderProfilePage />} />
          <Route path="/provider/:uuid/services" element={<ProviderServicesPage />} />

          {/* 인증 */}
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-in/email" element={<EmailSignIn />} />
          <Route path="/sign-up/agree" element={<SignUpAgree />} />
          <Route path="/sign-up/user-info" element={<SignUpUserInfo />} />

          {/* 커뮤니티 */}
          <Route path="/community" element={<Community />} />
          <Route path="/community/write" element={<CommunityForm />} />
          <Route path="/community/:uuid/edit" element={<CommunityForm />} />
          <Route path="/community/:uuid" element={<CommunityDetail />} />

          {/* 할랄 맵 */}
          <Route path="/halal-map" element={<HalalMap />} />

          {/* 비자 */}
          <Route path="/visa" element={<Visa />} />

          {/* 공항 출도착 */}
          <Route path="/airport" element={<Airport />} />

          {/* 할랄 배달 */}
          <Route path="/delivery" element={<DeliveryHome />} />
          <Route path="/delivery/orders" element={<DeliveryOrders />} />
          <Route path="/delivery/orders/:uuid" element={<DeliveryOrderDetail />} />
          <Route path="/delivery/:uuid" element={<DeliveryVendor />} />

          {/* 예약 */}
          <Route path="/booking/:uuid" element={<Booking />} />
          <Route path="/reservation" element={<Reservation />} />
          <Route path="/reservation/:uuid" element={<ReservationDetail />} />

          {/* 북마크 */}
          <Route path="/bookmark" element={<Bookmark />} />

          {/* 리뷰 */}
          <Route path="/review" element={<Reviews />} />
          <Route path="/review/:uuid" element={<ReviewDetail />} />

          {/* 알림 */}
          <Route path="/notification" element={<Notification />} />

          {/* 쿠폰 */}
          <Route path="/coupon" element={<Coupon />} />
          <Route path="/coupon/:uuid" element={<CouponDetail />} />

          {/* 비회원 예약 조회 */}
          <Route path="/guest-booking" element={<GuestBooking />} />

          {/* 회사 소개 */}
          <Route path="/about-us" element={<AboutUs />} />

          {/* 파트너 입점 문의 */}
          <Route path="/partner-inquiry" element={<PartnerInquiry />} />

          {/* 맞춤 여행 문의 */}
          <Route path="/trip-inquiry" element={<TripInquiry />} />

          {/* 정책 */}
          <Route path="/policy" element={<PolicyScreen />} />
          <Route path="/terms-of-service" element={<PolicyDetail />} />
          <Route path="/privacy-policy" element={<PolicyDetail />} />
          <Route path="/refund-policy" element={<PolicyDetail />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function Router() {
  return (
    <BrowserRouter>
      <ToastContainer
        position="top-center"
        autoClose={2500}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
      <GlobalErrorListener />
      <CustomDialog />
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export const ROUTES = {
  COMMUNITY_DETAIL: (uuid) => `/community/${uuid}`,
  COMMUNITY_WRITE: '/community/write',
  COMMUNITY_EDIT: (uuid) => `/community/${uuid}/edit`,
  BOOKING_DETAIL: (uuid) => `/booking/${uuid}`,
  RESERVATION_DETAIL: (uuid) => `/reservation/${uuid}`,
  COUPON_DETAIL: (uuid) => `/coupon/${uuid}`,
  GUEST_BOOKING: '/guest-booking',
  ABOUT_US: '/about-us',
  PARTNER_INQUIRY: '/partner-inquiry',
  TRIP_INQUIRY: '/trip-inquiry',
  PROVIDER_PROFILE: (uuid) => `/provider/${uuid}`,
  PROVIDER_SERVICES: (uuid) => `/provider/${uuid}/services`,
};
